import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MetricGauge from './MetricGauge';
import { CURRENCY_SYMBOLS, formatNumber, formatCurrencyValue, formatBillionWithCurrency } from '../utils/formatters';
import '../styles/StockCard.css';

const StockCard = ({ 
    stock, 
    onAction, 
    actionIcon, 
    actionType = "default", 
    isPortfolio = false,
    sortBy,
    onAddToPortfolio,
    showActions = true,
    initialExpanded = undefined
    }) => {
        const [activeMetricsTab, setActiveMetricsTab] = useState('valuation');
        const [isExpanded, setIsExpanded] = useState(() => {
            if (initialExpanded !== undefined) {
                return initialExpanded;
            }
            // Standard-Logik
            if (window.innerWidth < 768) return false;
            return true;
        });
    
    const calculateGrowth = () => {
        if (!stock.current_price || !stock.average_buy_price) return 0;
        return ((stock.current_price - stock.average_buy_price) / stock.average_buy_price) * 100;
    };

    const getNameFontSize = (name) => {
        const len = (name || '').length;
        if (len > 32) return '0.75rem';
        if (len > 22) return '0.875rem';
        return '1.0rem';
    };


    const periods = [
        { key: 'daily_change', label: '1T' },
        { key: 'weekly_change', label: '1W' },
        { key: 'monthly_change', label: '1M' },
        { key: 'yearly_change', label: '1Y' }
    ];

    const getDisplayValue = () => {
        switch(sortBy) {
            case 'total_growth':
                return `${formatNumber(stock.growth)}%`;
            case 'daily_change':
                return `${formatNumber(stock.daily_change)}%`;
            case 'weekly_change':
                return `${formatNumber(stock.weekly_change)}%`;
            case 'monthly_change':
                return `${formatNumber(stock.monthly_change)}%`;
            case 'yearly_change':
                return `${formatNumber(stock.yearly_change)}%`;
            case 'value':
                return `${formatCurrencyValue(stock.total_value, stock.display_currency)}`;
            case 'dividend':
                return `${formatCurrencyValue(stock.dividend, stock.display_currency)}`;
            case 'dividend_yield':
                return `${formatNumber(stock.dividend_yield)}%`;
            case 'trailing_p_e':
                return formatNumber(stock.trailing_p_e);
            case 'forward_p_e':
                return formatNumber(stock.forward_p_e);
            case 'price_to_book':
                return formatNumber(stock.price_to_book);
            case 'profit_margin':
                return `${formatNumber(stock.profit_margin * 100)}%`;
            case 'revenue':
                return formatBillionWithCurrency(stock.revenue, stock.display_currency);
            case 'market_cap':
                return formatBillionWithCurrency(stock.market_cap, stock.display_currency);
            case 'next_year_growth':
                return `${formatNumber(stock.growth_estimates?.next_year * 100)}%`;
            case 'revenue_growth':
                return `${formatNumber(stock.revenue_growth *100 )}%`;
            case 'earnings_growth':
                return `${formatNumber(stock.earnings_growth *100 )}%`;
            case 'current_price':
                return formatCurrencyValue(stock.current_price, stock.display_currency);
            default:
                return formatCurrencyValue(stock.current_price, stock.display_currency);
        }
    };
    
    const getSortLabel = () => {
        switch(sortBy) {
            case 'total_growth':
                return 'Gesamtwachstum';
            case 'daily_change':
                return '24h Änderung';
            case 'weekly_change':
                return '7d Änderung';
            case 'monthly_change':
                return '30d Änderung';
            case 'yearly_change':
                return '1J Änderung';
            case 'value':
                return 'Gesamtwert';
            case 'dividend':
                return 'Dividende';
            case 'dividend_yield':
                return 'Dividendenrendite';
            case 'trailing_p_e':
                return 'KGV';
            case 'price_to_book':
                return "KBV";
            case 'forward_p_e':
                return 'KGV (erw.)';
            case 'profit_margin':
                return 'Gewinnmarge';
            case 'revenue':
                return 'Umsatz';
            case 'market_cap':
                return 'Marktkapitalisierung';
            case 'next_year_growth':
                return 'Erw. Wachstum (Jahr)';
            case 'revenue_growth':
                return 'Umsatzwachstum';
            case 'earnings_growth':
                return 'Gewinnwachstum';
            case 'current_price':
                return 'Aktueller Preis';
            default:
                return 'Aktueller Kurs';
        }
    };

    const calculateCategoryScores = () => {
        // Calculate average scores for each category
        const ms = stock.metricScores || {};
        const avg = (keys) => Math.round(keys.reduce((sum, k) => sum + (ms[k]?.score || 0), 0) / keys.length);

        const valuationScore = avg(['trailing_p_e', 'forward_p_e', 'price_to_book', 'enterprise_to_ebitda']);
        const financialScore = avg(['operating_margin', 'profit_margin', 'gross_margin', 'return_on_equity']);
        const dividendScore = avg(['dividend', 'dividend_yield', 'payout_ratio']);
        const growthScore = avg(['revenue_growth', 'earnings_growth', 'next_year_growth', 'current_year_growth']);
      
        return {
          valuationScore,
          financialScore, 
          dividendScore,
          growthScore
        };
    };

    // Now that we've defined calculateCategoryScores, we can safely use it
    const categoryScores = calculateCategoryScores();
      
    const renderPeriodGrid = () => (
        <div className="card-section performance-section">
            <h4 className="card-section-title">Performance Übersicht</h4>
            <div className="metrics-grid">
                {periods.map(period => {
                    const value = Number(stock[period.key]) || 0;
                    return (
                        <div key={period.key} className="metric">
                            <label>{period.label}</label>
                            <div className="metric-value-row">
                                <span className={value > 0 ? 'positive' : value < 0 ? 'negative' : ''}>
                                    <i className={`fas fa-${value > 0 ? 'arrow-up' : value < 0 ? 'arrow-down' : 'minus'}`} aria-hidden="true"></i>
                                    {' '}{formatNumber(Math.abs(value))}%
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );


    // Replace both renderValuationMetrics and renderFinancialHealth functions with this new combined function:
    const renderCombinedMetrics = () => (
        <div className="card-section">
            <h4 className="card-section-title">Kennzahlen</h4>
            <div className="metrics-tabs">
                <button 
                    type="button"
                    className={`tab-button ${activeMetricsTab === 'valuation' ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setActiveMetricsTab('valuation'); }}
                >
                    Bewertung
                </button>
                <button 
                    type="button"
                    className={`tab-button ${activeMetricsTab === 'financial' ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setActiveMetricsTab('financial'); }}
                >
                    Finanzen
                </button>
            </div>
            
            <div className="metrics-content">
                {activeMetricsTab === 'valuation' ? (
                    <div className="metrics-grid">
                        <div className="metric">
                            <label>KGV</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.trailing_p_e)}</span>
                                <MetricGauge score={stock.metricScores?.trailing_p_e?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>KGV (erwartet)</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.forward_p_e)}</span>
                                <MetricGauge score={stock.metricScores?.forward_p_e?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>KBV</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.price_to_book)}</span>
                                <MetricGauge score={stock.metricScores?.price_to_book?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Wert/EBITDA</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.enterprise_to_ebitda)}</span>
                                <MetricGauge score={stock.metricScores?.enterprise_to_ebitda?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Marktkapitalisierung</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.market_cap, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.market_cap?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Unternehmenswert</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.enterprise_value, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.enterprise_value?.score || 0} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="metrics-grid">
                        <div className="metric">
                            <label>Umsatz</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.revenue, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.revenue?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Operative Marge</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.operating_margin * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.operating_margin?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Gewinnmarge</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.profit_margin * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.profit_margin?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Bruttomarge</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.gross_margin * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.gross_margin?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Eigenkapitalrendite</label>
                            <div className="metric-value-row">
                                <span>{formatNumber(stock.return_on_equity * 100)}%</span>
                                <MetricGauge score={stock.metricScores?.return_on_equity?.score || 0} />
                            </div>
                        </div>
                        <div className="metric">
                            <label>Free Cashflow</label>
                            <div className="metric-value-row">
                                <span>{formatBillionWithCurrency(stock.free_cash_flow, stock.display_currency)}</span>
                                <MetricGauge score={stock.metricScores?.free_cash_flow?.score || 0} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
    

    const renderDividendSection = () => (
        <div className="card-section dividend-section">
            <h4 className="card-section-title">Dividenden</h4>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Dividende</label>
                    <div className="metric-value-row">
                        <span>{formatCurrencyValue(stock.dividend, stock.display_currency)}</span>
                        <MetricGauge score={stock.metricScores?.dividend?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Dividendenrendite</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.dividend_yield)}%</span>
                        <MetricGauge score={stock.metricScores?.dividend_yield?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Ausschüttungsquote</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.payout_ratio)}%</span>
                        <MetricGauge score={stock.metricScores?.payout_ratio?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Letzte Ausschüttung</label>
                    <div className="metric-value-row">
                        <span>{stock.last_dividend_date ? new Date(stock.last_dividend_date).toLocaleDateString('de-DE') : 'N/A'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderGrowthEstimates = () => (
        <div className="card-section growth-estimates">
            <h4 className="card-section-title">Wachstumsprognosen</h4>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Aktuelles Quartal</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.current_qtr * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.current_qtr_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Nächstes Quartal</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.next_qtr * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.next_qtr_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Aktuelles Jahr</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.current_year * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.current_year_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Nächstes Jahr</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.growth_estimates?.next_year * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.next_year_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Umsatzwachstum</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.revenue_growth * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.revenue_growth?.score || 0} />
                    </div>
                </div>
                <div className="metric">
                    <label>Gewinnwachstum</label>
                    <div className="metric-value-row">
                        <span>{formatNumber(stock.earnings_growth * 100)}%</span>
                        <MetricGauge score={stock.metricScores?.earnings_growth?.score || 0} />
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderPortfolioMetrics = () => (
        <div className="card-section portfolio-section">
            <h4 className="card-section-title">Portfolio Details</h4>
            <div className="metrics-grid">
                <div className="metric">
                    <label>Stückzahl</label>
                    <div className="metric-value-row">
                        <span>{stock.quantity}</span>
                    </div>
                </div>
                <div className="metric">
                    <label>Durchschn. Kaufpreis</label>
                    <div className="metric-value-row">
                        <span>{CURRENCY_SYMBOLS[stock.display_currency] || stock.display_currency}{formatNumber(stock.average_buy_price)}</span>
                    </div>
                </div>
                <div className="metric">
                    <label>Aktueller Preis</label>
                    <div className="metric-value-row">
                        <span>{CURRENCY_SYMBOLS[stock.display_currency] || stock.display_currency}{formatNumber(stock.current_price)}</span>
                    </div>
                </div>
                <div className="metric">
                    <label>Entwicklung seit Kauf</label>
                    <div className="metric-value-row">
                        <span className={calculateGrowth() >= 0 ? 'positive' : 'negative'}>{formatNumber(calculateGrowth())}%</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const showCornerButton = showActions && (
        (isPortfolio && onAction) || (!isPortfolio && onAddToPortfolio)
    );

    return (
        <article
            className={`stock-card${isExpanded ? '' : ' stock-card-collapsed'}`}
            onClick={() => setIsExpanded(prev => !prev)}
        >
            {/* Corner Action Button */}
            {showCornerButton && (
                <button 
                    type="button"
                    className={`corner-action-btn ${isPortfolio ? 'remove' : 'add'}`}
                    aria-label={isPortfolio ? 'Aus Portfolio entfernen' : 'Zum Portfolio hinzufügen'}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isPortfolio && onAction) {
                            onAction(stock.id);
                        } else if (!isPortfolio && onAddToPortfolio) {
                            onAddToPortfolio(stock);
                        }
                    }}
                    title={isPortfolio ? 'Aus Portfolio entfernen' : 'Zum Portfolio hinzufügen'}
                >
                    <i className={`fas ${isPortfolio ? 'fa-times' : 'fa-plus'}`} aria-hidden="true"></i>
                </button>
            )}
            <div className="stock-card-header">
                <div className="stock-card-title">
                    <Link
                        to={`/stock-detail/${stock.id}`}
                        className="stock-title-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="stock-card-name" style={{ fontSize: getNameFontSize(stock.name) }}>{stock.name}</h3>
                    </Link>
                    {stock.sector && (
                        <span className="stock-sector-compact">{stock.sector}</span>
                    )}
                </div>
            </div>
            <div className="stock-card-value-row">
                <span className="stock-value">{getDisplayValue()}</span>
                {sortBy && <span className="sort-label">{getSortLabel()}</span>}
            </div>
            <div className="stock-card-summary">
                {stock.metricScores && (
                    <div className="summary-grid">
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.valuationScore} summaryGauge={true} label="Bewertung" />
                        </div>
                        <label>Bewertung</label>
                    </div>
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.financialScore} summaryGauge={true} label="Finanzen" />
                        </div>
                        <label>Finanzen</label>
                    </div>
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.dividendScore} summaryGauge={true} label="Dividenden" />
                        </div>
                        <label>Dividenden</label>
                    </div>
                    <div className="summary-item">
                        <div className="summary-gauge">
                        <MetricGauge score={categoryScores.growthScore} summaryGauge={true} label="Wachstum" />
                        </div>
                        <label>Wachstum</label>
                    </div>
                    </div>
                )}
            </div>
            <button
                type="button"
                className="stock-card-expand-bar"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(prev => !prev);
                }}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Kennzahlen ausblenden' : 'Kennzahlen anzeigen'}
            >
                <i className={`fas fa-chevron-down stock-card-expand-icon${isExpanded ? ' rotated' : ''}`} aria-hidden="true" />
                <span>{isExpanded ? 'Schließen' : 'Kennzahlen'}</span>
            </button>
            <div
                className={`stock-card-content${isExpanded ? ' is-open' : ''}`}
                onClick={(e) => e.stopPropagation()}
                aria-hidden={!isExpanded}
            >
                {renderPeriodGrid()}
                {isPortfolio && renderPortfolioMetrics()}
                {renderCombinedMetrics()}
                {renderDividendSection()}
                {renderGrowthEstimates()}
            </div>
        </article>
    );
};

export default StockCard;